import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calculator, Heart, Sparkles, Compass, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { aboutTranslations } from '../translations/about';
import { useSeo } from '../hooks/useSeo';

export const About = () => {
  const { language } = useLanguage();
  const t = aboutTranslations[language];

  const isFr = language === 'fr';
  useSeo(
    isFr ? "Stef Ruiz | Numérologie & accompagnement à Ibiza et en visio" : "Comprendre qui l'on est - Stef Ruiz",
    isFr ? "Découvrez le parcours et l'approche de Stef Ruiz en numérologie et accompagnement individuel, couple et famille à Ibiza et en visio." : "A product of emergent.sh"
  );

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section - Bannière */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/footprints-beach.jpg)',
            backgroundPosition: '50% 50%',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/50 via-gray-900/40 to-gray-900/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p className="text-xl sm:text-2xl text-white/95 leading-relaxed font-light">
            {t.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Mon histoire */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-12">
            {/* Photo professionnelle */}
            <div className="flex-shrink-0">
              <img 
                src="/stef-professional-2025.jpg" 
                alt="Stéphanie Ruiz" 
                className="w-64 h-64 md:w-80 md:h-80 rounded-2xl object-cover shadow-2xl"
              />
            </div>
            
            {/* Texte */}
            <div className="flex-1">
              <h2 className="text-3xl md:text-4xl font-light text-teal-600 mb-8">
                {t.story.title}
              </h2>
              
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  {t.story.intro} <strong className="text-gray-900">{t.story.name}</strong>{t.story.role}
                </p>
                
                <p>
                  {t.story.p1}
                </p>
                
                <p>
                  {t.story.p2} <strong className="text-teal-600">{t.story.approaches}</strong>.
                </p>
                
                <p>
                  {t.story.p3}
                </p>
                
                <p>
                  {t.story.p4}
                </p>
                
                <p>
                  {t.story.p5} <strong className="text-teal-600">{t.story.highlight}</strong> {t.story.p5end}
                </p>
                
                <p className="text-xl font-light italic text-gray-900 pt-4">
                  {t.story.quote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mes domaines d'accompagnement */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-pink-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-center text-teal-600 mb-4">
            {t.domains.title}
          </h2>
          <p className="text-center text-gray-600 mb-16 text-lg max-w-2xl mx-auto">
            {t.domains.subtitle}
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Numérologie */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 bg-white">
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mb-6 shadow-lg">
                  <Calculator size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-4">{t.domains.numerology.title}</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {t.domains.numerology.description}
                </p>
              </CardContent>
            </Card>

            {/* Accompagnement de couple et familial */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 bg-white">
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center mb-6 shadow-lg">
                  <Heart size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-4">{t.domains.therapy.title}</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {t.domains.therapy.description}
                </p>
              </CardContent>
            </Card>

            {/* Coaching de vie */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 bg-white">
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mb-6 shadow-lg">
                  <Compass size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-4">{t.domains.coaching.title}</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {t.domains.coaching.description}
                </p>
              </CardContent>
            </Card>

            {/* Accompagnement énergétique */}
            <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 bg-white">
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center mb-6 shadow-lg">
                  <Sparkles size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-teal-600 mb-4">{t.domains.energy.title}</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  {t.domains.energy.description}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mes valeurs */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-center text-teal-600 mb-16">
            {t.values.title}
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Authenticité */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                  <Check size={24} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-teal-600 mb-3">{t.values.authenticity.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.values.authenticity.description}
              </p>
            </div>

            {/* Empathie */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-fuchsia-500 flex items-center justify-center">
                  <Heart size={24} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-teal-600 mb-3">{t.values.empathy.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.values.empathy.description}
              </p>
            </div>

            {/* Clarté */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
                  <Sparkles size={24} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-teal-600 mb-3">{t.values.clarity.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.values.clarity.description}
              </p>
            </div>

            {/* Transformation */}
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                  <Compass size={24} className="text-white" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-teal-600 mb-3">{t.values.transformation.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.values.transformation.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-600 via-teal-500 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light mb-8 leading-tight">
            {t.cta.title}
          </h2>
          <p className="text-lg md:text-xl mb-12 text-white/95 leading-relaxed font-light max-w-3xl mx-auto">
            {t.cta.subtitle}
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-teal-600 text-white hover:bg-teal-700 text-xl px-14 py-10 h-auto shadow-2xl transition-all duration-300 hover:scale-105">
              {t.cta.button}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
